import React from 'react';

import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { EventTypeId } from '@swf/sports/types/eventType';
import type { LiveData } from '@swf/sports/utils/getLiveData';
import {
  getIsAmericanFootballLiveData,
  getIsBaseballLiveData,
  getIsBasketballLiveData,
  getIsDartsLiveData,
  getIsESportsLiveData,
  getIsFootballLiveData,
  getIsIceHockeyLiveData,
  getIsTableTennisLiveData,
  getIsTennisLiveData
} from '@swf/sports/utils/getLiveData';

import EventStatusMessage from '../EventStatusMessage/EventStatusMessage';

import AmericanFootballScoreboardInPlayStatus from './AmericanFootballScoreboardInPlayStatus/AmericanFootballScoreboardInPlayStatus';
import BaseballScoreboardInPlayStatus from './BaseballScoreboardInPlayStatus/BaseballScoreboardInPlayStatus';
import BasketballScoreboardInPlayStatus from './BasketballScoreboardInPlayStatus/BasketballScoreboardInPlayStatus';
import FootballScoreboardInPlayStatus from './FootballScoreboardInPlayStatus/FootballScoreboardInPlayStatus';
import IceHockeyScoreboardInPlayStatus from './IceHockeyScoreboardInPlayStatus/IceHockeyScoreboardInPlayStatus';
import TennisScoreboardInPlayStatus from './TennisScoreboardInPlayStatus/TennisScoreboardInPlayStatus';

interface Props {
  liveData: LiveData;
  eventTypeId: EventTypeId;
}

const ScoreboardInPlayStatus: React.FC<Props> = ({ liveData, eventTypeId }) => {
  const { isError } = useLiveData();

  if (
    isError ||
    /**
     * Temporarily return the default 'In Play' message for baseball until the RCP clock issue is resolved
     */
    getIsBaseballLiveData(eventTypeId, liveData) ||
    getIsDartsLiveData(eventTypeId, liveData) ||
    getIsESportsLiveData(eventTypeId, liveData) ||
    getIsTableTennisLiveData(eventTypeId, liveData)
  ) {
    return <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size="XSmall" />;
  }

  if (getIsAmericanFootballLiveData(eventTypeId, liveData)) {
    return <AmericanFootballScoreboardInPlayStatus americanFootballFixture={liveData} />;
  }

  if (getIsBaseballLiveData(eventTypeId, liveData)) {
    return <BaseballScoreboardInPlayStatus baseballFixture={liveData} />;
  }

  if (getIsBasketballLiveData(eventTypeId, liveData)) {
    return <BasketballScoreboardInPlayStatus basketballFixture={liveData} />;
  }

  if (getIsFootballLiveData(eventTypeId, liveData)) {
    return <FootballScoreboardInPlayStatus footballFixture={liveData} />;
  }

  if (getIsIceHockeyLiveData(eventTypeId, liveData)) {
    return <IceHockeyScoreboardInPlayStatus iceHockeyFixture={liveData} />;
  }

  if (getIsTennisLiveData(eventTypeId, liveData)) {
    return <TennisScoreboardInPlayStatus tennisMatch={liveData} />;
  }

  return null;
};

export default ScoreboardInPlayStatus;
